import React from "react";
import { motion } from "framer-motion";

/**
 * The companion's mascot: an original hatching-egg sprite (cream shell,
 * pastel freckles, a cracked cap it wears like a hat, two bright eyes).
 * Pure SVG + framer-motion so it is tiny and themeable.
 */
export const EggSprite = ({ size = 72, mood = "idle", className = "" }) => {
  const happy = mood === "happy";
  const busy = mood === "busy";
  return (
    <svg
      viewBox="0 0 64 80"
      width={size}
      height={(size * 80) / 64}
      className={className}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="xzEggShell" cx="38%" cy="32%" r="75%">
          <stop offset="0" stopColor="#fffaf0" />
          <stop offset="1" stopColor="#f1dcae" />
        </radialGradient>
        <radialGradient id="xzEggCap" cx="40%" cy="30%" r="80%">
          <stop offset="0" stopColor="#fff3cf" />
          <stop offset="1" stopColor="#e8c98a" />
        </radialGradient>
      </defs>
      {/* shadow */}
      <ellipse cx="32" cy="76" rx="18" ry="3.5" fill="#000" opacity="0.18" />
      {/* body */}
      <path
        d="M32 8 C48 8 57 32 57 49 C57 65 46 75 32 75 C18 75 7 65 7 49 C7 32 16 8 32 8 Z"
        fill="url(#xzEggShell)"
        stroke="#d6b97f"
        strokeWidth="2"
      />
      {/* freckles */}
      <circle cx="18" cy="56" r="2.6" fill="#f9a8d4" opacity="0.85" />
      <circle cx="47" cy="58" r="2.2" fill="#93c5fd" opacity="0.85" />
      <circle cx="40" cy="68" r="1.8" fill="#86efac" opacity="0.85" />
      <circle cx="24" cy="66" r="1.6" fill="#fde68a" opacity="0.9" />
      {/* blush */}
      <ellipse cx="19" cy="46" rx="3.6" ry="2.2" fill="#fb7185" opacity="0.45" />
      <ellipse cx="45" cy="46" rx="3.6" ry="2.2" fill="#fb7185" opacity="0.45" />
      {/* eyes (blink) */}
      <motion.g
        style={{ originX: "32px", originY: "40px" }}
        animate={{ scaleY: [1, 1, 0.08, 1, 1] }}
        transition={{ repeat: Infinity, duration: 4.6, times: [0, 0.88, 0.92, 0.96, 1], ease: "easeInOut" }}
      >
        <ellipse cx="25" cy="40" rx="3" ry={busy ? 2.2 : 3.6} fill="#1f2937" />
        <ellipse cx="39" cy="40" rx="3" ry={busy ? 2.2 : 3.6} fill="#1f2937" />
        <circle cx="26.2" cy="38.6" r="1" fill="#fff" />
        <circle cx="40.2" cy="38.6" r="1" fill="#fff" />
      </motion.g>
      {/* mouth */}
      {happy ? (
        <path d="M27 48 Q32 53 37 48" stroke="#9a3412" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      ) : busy ? (
        <ellipse cx="32" cy="49" rx="2.4" ry="2" fill="#9a3412" opacity="0.8" />
      ) : (
        <path d="M28.5 48.5 Q32 51 35.5 48.5" stroke="#9a3412" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      )}
      {/* cracked cap worn like a hat */}
      <motion.g
        style={{ originX: "32px", originY: "22px" }}
        animate={{ rotate: happy ? [0, -6, 6, 0] : [0, -2, 0] }}
        transition={{ repeat: Infinity, duration: happy ? 1.2 : 3.2, ease: "easeInOut" }}
      >
        <path
          d="M15 27 L20 20 L25 26 L30 18 L35 25 L40 18 L45 25 L50 21 L53 28 C50 23 44 11 32 11 C20 11 14 23 15 27 Z"
          fill="url(#xzEggCap)"
          stroke="#d6b97f"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </motion.g>
      {/* feet */}
      <ellipse cx="24" cy="74" rx="5" ry="2.4" fill="#f59e0b" />
      <ellipse cx="40" cy="74" rx="5" ry="2.4" fill="#f59e0b" />
    </svg>
  );
};

/** The "wake ball" the companion sleeps in (header button + hatch animation). */
export const WakeBall = ({ size = 22, open = false, className = "" }) => (
  <svg viewBox="0 0 40 40" width={size} height={size} className={className} aria-hidden="true">
    <motion.g
      style={{ originX: "20px", originY: "20px" }}
      animate={{ rotate: open ? -38 : 0, y: open ? -6 : 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 18 }}
    >
      <path d="M2 20 A18 18 0 0 1 38 20 Z" fill="#facc15" stroke="#1f2937" strokeWidth="2.5" />
    </motion.g>
    <path d="M2 20 A18 18 0 0 0 38 20 Z" fill="#e5e7eb" stroke="#1f2937" strokeWidth="2.5" />
    <rect x="2" y="18.5" width="36" height="3" fill="#1f2937" />
    <circle cx="20" cy="20" r="5.5" fill="#f3f4f6" stroke="#1f2937" strokeWidth="2.5" />
    <circle cx="20" cy="20" r="2.2" fill="#22d3ee" />
  </svg>
);
